import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, defaultLanguage, supportedLanguages } from './translations';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved && supportedLanguages.includes(saved)) {
      return saved;
    }
    return defaultLanguage;
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Get translation by key path
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to default language
        value = translations[defaultLanguage];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key;
          }
        }
        break;
      }
    }
    
    return value || key;
  };

  // Get data array (skills, missions, employers, etc.)
  const getData = (dataKey) => {
    return translations[language][dataKey] || translations[defaultLanguage][dataKey] || [];
  };

  // Get profile data
  const getProfile = () => translations[language].profile || translations[defaultLanguage].profile;
  
  // Get highlights
  const getHighlights = () => translations[language].highlights || translations[defaultLanguage].highlights;
  
  // Get skills data
  const getSkills = () => translations[language].skillsData || translations[defaultLanguage].skillsData;
  
  // Get employers data
  const getEmployers = () => translations[language].employersData || translations[defaultLanguage].employersData;
  
  // Get missions data
  const getMissions = () => translations[language].missionsData || translations[defaultLanguage].missionsData;
  
  // Get mission details
  const getMissionDetails = () => translations[language].missionDetails || translations[defaultLanguage].missionDetails;
  
  // Get contact data
  const getContact = () => translations[language].contactData || translations[defaultLanguage].contactData;
  
  // Get certifications data
  const getCertifications = () => translations[language].certificationsData || translations[defaultLanguage].certificationsData;
  
  // Get AI projects data
  const getAIProjects = () => translations[language].aiProjectsData || translations[defaultLanguage].aiProjectsData;
  
  // Get sectors
  const getSectors = () => translations[language].sectors || translations[defaultLanguage].sectors;
  
  // Get countries
  const getCountries = () => translations[language].countries || translations[defaultLanguage].countries;

  const changeLanguage = (lang) => {
    if (supportedLanguages.includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      t, 
      changeLanguage,
      getData,
      getProfile,
      getHighlights,
      getSkills,
      getEmployers,
      getMissions,
      getMissionDetails,
      getContact,
      getCertifications,
      getAIProjects,
      getSectors,
      getCountries
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
